import { FC, memo, useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  message,
  Row,
  Space,
  TimePicker,
  Typography
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useDoctorProfile } from 'providers/DoctorProfile';
import { updateWorkingHours } from 'services/api/profile';

import { WeekDay } from './styled';
import { DayStateType } from './types';
import { IconButton } from 'components/ui';
import { parseJson } from 'utils/json';
import { showRestError } from 'utils/showErrorMessage';

const DAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday'
];

export const initialWorkingHours = {
  sunday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  monday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  tuesday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  wednesday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  thursday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  friday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  },
  saturday: {
    enabled: true,
    hours: [
      {
        start: '00:00',
        end: '23:30'
      }
    ]
  }
};

const { Title } = Typography;

const WorkingHours: FC = () => {
  // translations
  const { t } = useTranslation();
  // states
  const [data, setData] = useState<DayStateType>(initialWorkingHours);
  const { profile, refresh } = useDoctorProfile();

  useEffect(() => {
    const workingHours = parseJson(profile?.working_hours);

    if (workingHours) {
      setData(workingHours);
    }
  }, [profile?.working_hours]);

  const onTimeSelect = useCallback(
    (day: string, i: number, type: 'start' | 'end', value: Dayjs | null) => {
      if (!value) return;

      setData(prev => {
        const current = prev[day] || {};

        const hours = [...current.hours];
        hours[i] = {
          ...hours[i],
          [type]: value.format('HH:mm')
        };

        return {
          ...prev,
          [day]: {
            ...current,
            hours
          }
        };
      });
    },
    []
  );

  const onSelectDay = useCallback((day: string, value: boolean) => {
    setData(prev => {
      return {
        ...prev,
        [day]: {
          enabled: value,
          hours: [
            {
              start: '00:00',
              end: '23:30'
            }
          ]
        }
      };
    });
  }, []);

  const addHour = useCallback((day: string) => {
    setData(prev => {
      const current = prev[day] || {};
      const prevEnd =
        +current.hours[current.hours.length - 1].end.split(':')[0] + 1;

      const start = `${prevEnd > 0 ? prevEnd : `${prevEnd}:00`}:00`;

      return {
        ...prev,
        [day]: {
          ...current,
          hours: [
            ...current.hours,
            {
              start,
              end: '23:30'
            }
          ]
        }
      };
    });
  }, []);

  const deleteHour = useCallback((day: string, i: number) => {
    setData(prev => {
      const current = prev[day] || {};
      const hours = current.hours.filter((_, index) => index !== i);

      return {
        ...prev,
        [day]: {
          ...current,
          hours
        }
      };
    });
  }, []);

  const getDisabledStartTime = useCallback(
    (day: string, index: number) => {
      if (index < 1) return {};

      const prevEndHour = +data[day].hours[index - 1].end.split(':')[0];
      const disabledHours: number[] = [];

      for (let i = 0; i <= prevEndHour; i++) {
        disabledHours.push(i);
      }

      return {
        disabledHours: () => disabledHours
      };
    },
    [data]
  );

  const getDisabledEndTime = useCallback(
    (day: string, index: number) => {
      const startHour = +data[day].hours[index].start.split(':')[0];

      const disabledHours: number[] = [];

      for (let i = 0; i <= startHour; i++) {
        disabledHours.push(i);
      }

      return {
        disabledHours: () => disabledHours
      };
    },
    [data]
  );

  const handleSave = useCallback(() => {
    // whole-blob replace of the day-keyed jsonb
    updateWorkingHours(data)
      .then(() => {
        refresh();
        message.success(t('messages.successfully_saved'));
      })
      .catch(err => showRestError(err));
  }, [data, refresh, t]);

  return (
    <Card>
      <Space size={16} direction="vertical" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle">
          <Space size={12}>
            {DAYS.map(item => {
              const isSelected = data[item].enabled;

              return (
                <WeekDay
                  onClick={() => onSelectDay(item, !isSelected)}
                  isSelected={isSelected}
                  key={`day-button-${item}`}
                >
                  {t(`dates.${item[0]}`)}
                </WeekDay>
              );
            })}
          </Space>
          <Button type="primary" onClick={handleSave}>
            {t('common.save')}
          </Button>
        </Row>
        {DAYS.map(item => {
          const isSelected = data[item].enabled;
          if (!isSelected) return null;

          return (
            <div key={`day-item-${item}`}>
              <Title level={4}>{t(`dates.${item}`)}</Title>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {data[item].hours.map((hour, i, arr) => {
                  const endHour = +hour.end.split(':')[0];

                  return (
                    <Space key={`day-item-hour-${item}-${i}`}>
                      <TimePicker
                        onChange={e => onTimeSelect(item, i, 'start', e)}
                        value={dayjs(hour.start, 'HH:mm')}
                        format="HH:mm"
                        minuteStep={30}
                        showNow={false}
                        inputReadOnly
                        disabledTime={() => getDisabledStartTime(item, i)}
                      />
                      <TimePicker
                        onChange={e => onTimeSelect(item, i, 'end', e)}
                        value={dayjs(hour.end, 'HH:mm')}
                        format="HH:mm"
                        minuteStep={30}
                        showNow={false}
                        inputReadOnly
                        disabledTime={() => getDisabledEndTime(item, i)}
                      />
                      {i === arr.length - 1 && endHour < 23 && (
                        <IconButton onClick={() => addHour(item)}>
                          <PlusOutlined />
                        </IconButton>
                      )}
                      {i > 0 && (
                        <IconButton onClick={() => deleteHour(item, i)}>
                          <MinusOutlined />
                        </IconButton>
                      )}
                    </Space>
                  );
                })}
              </Space>
            </div>
          );
        })}
      </Space>
    </Card>
  );
};

export default memo(WorkingHours);
